import type { PropsOf } from '@emotion/react';
import styled from '@emotion/styled';
import type { DatePicker } from '@innovationdepartment/proxima-ui';
import type {
  HeatmapViewIntensity,
  InsightsColumnData,
  InsightsInterval,
  IntervalOption
} from 'types/insights';
import type { MetaAdAccount } from 'types/stores/ad-account-store';
import { InsightsHeader } from '../SharedLayout';
import { TrendsSubheader } from './Layout';
import { TrendsTable } from './Table';

const TrendsInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px;
  background-color: white;
  flex-grow: 100;
`;

const TrendsStickyTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
`;

const flavorCategoryOptions = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'Beauty',
    value: 'BEAU'
  },
  {
    label: 'Fashion & Accessories',
    value: 'FASH'
  },
  {
    label: 'Food & Drink',
    value: 'FOOD'
  },
  {
    label: 'Health',
    value: 'HEAL'
  },
  {
    label: 'Home',
    value: 'HOME'
  }
];

const heatmapViewIntesityOptions: { label: string; value: HeatmapViewIntensity }[] = [
  {
    label: 'Bright Heatmap',
    value: 'bright'
  },
  {
    label: 'Light Heatmap',
    value: 'light'
  }
];

type TrendsViewProps = {
  brandName: string;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  metaAdAccount: MetaAdAccount | null;
  adAccounts: MetaAdAccount[];
  interval: InsightsInterval;
  insightsColumnData: InsightsColumnData[];
  intervalLabel: string;
  flavorCategory: string;
  heatmapViewIntesity: HeatmapViewIntensity;
  intervalOptions: IntervalOption[];
  onDateRangeSelect: PropsOf<typeof DatePicker>['onDateRangeSelect'];
  onIntervalChanged: (interval: InsightsInterval) => void;
  onFlavorCategoryChanged: (flavorCategory: string) => void;
  onHeatmapViewIntesityChanged: (view: HeatmapViewIntensity) => void;
  onAdAccountSelect: (adAccountId: string) => void;
};

const TrendsView = (props: TrendsViewProps) => {
  const {
    brandName,
    dateRange,
    interval,
    intervalLabel,
    intervalOptions,
    insightsColumnData,
    flavorCategory,
    heatmapViewIntesity,
    onDateRangeSelect,
    onFlavorCategoryChanged,
    onHeatmapViewIntesityChanged,
    onIntervalChanged,
    adAccounts,
    metaAdAccount,
    onAdAccountSelect
  } = props;

  return (
    <>
      <TrendsStickyTop>
        <InsightsHeader brandName={brandName} title="Performance Trends" helpLink="" />
        <TrendsSubheader
          dateRange={dateRange}
          interval={interval}
          intervalOptions={intervalOptions}
          onDateRangeSelect={onDateRangeSelect}
          onIntervalChanged={onIntervalChanged}
          adAccounts={adAccounts}
          metaAdAccount={metaAdAccount}
          onAdAccountSelect={onAdAccountSelect}
        />
      </TrendsStickyTop>
      <TrendsInnerContainer>
        <TrendsTable
          insightsData={insightsColumnData}
          intervalLabel={intervalLabel}
          flavorCategory={flavorCategory}
          heatmapViewIntesity={heatmapViewIntesity}
          flavorCategoryOptions={flavorCategoryOptions}
          heatmapViewIntesityOptions={heatmapViewIntesityOptions}
          onFlavorCategoryChanged={onFlavorCategoryChanged}
          onHeatmapViewIntesityChanged={onHeatmapViewIntesityChanged}
        />
      </TrendsInnerContainer>
    </>
  );
};

export default TrendsView;
